/**
 * Feature registry — static declaration of every POS-side feature that vertical
 * presets (Session 21.2) and per-org feature flags (Session 21.3) can toggle.
 *
 * IDs use dot-notation `<module>.<feature>` so the flag surface can be grouped
 * by module in the dashboard matrix (Session 21.3 delivers that UI).
 *
 * This module intentionally ships the declaration surface only. Phase 21.3
 * layers the runtime `FeatureFlagsClient` on top; the RN POS + Web POS read
 * from both the preset + the per-org override before gating UI.
 *
 * Phase 36 Session 36.10 — every `defineFeature(...)` call gains a `suite`
 * field that ties the feature to a product-suite identifier (`pos`, `kds`,
 * `invoice`, `payroll`, `inventory`, `marketing`, `reporting`, `core`).
 * `core` features are always available; non-core suites are entitled per
 * subscription plan via `billing.subscription_plans.entitled_suites`. The
 * resolver in `FeatureFlagService` short-circuits unentitled suites to
 * `false` regardless of preset / override / default.
 *
 * Module → suite mapping decision (2026-05-04):
 *   `pos`        → `pos`        (1:1)
 *   `kds`        → `kds`        (1:1)
 *   `invoice`    → `invoice`    (1:1)
 *   `payroll`    → `payroll`    (1:1)
 *   `reporting`  → `reporting`  (1:1)
 *   `marketing`  → `marketing`  (1:1)
 *   `automation` → `core`       (cross-cutting infrastructure)
 *   `hardware`   → `core`       (terminals, printers, etc.)
 */

import type { SuiteId } from '@commerce-os/shared-types';

/** Feature module groupings. Matches the 16.2 role-matrix module axis. */
export type FeatureModule =
  | 'pos'
  | 'kds'
  | 'invoice'
  | 'payroll'
  | 'automation'
  | 'hardware'
  | 'reporting'
  | 'inventory'
  | 'marketing'
  // Phase 50 — field service management (jobs, dispatch, work orders).
  | 'field_service'
  // Phase 57 — in-house general ledger.
  | 'accounting'
  // Phase 64 — the multi-jurisdiction tax layer (Growth+).
  | 'tax'
  // Phase 64 — SSO / SAML configuration (Enterprise).
  | 'auth'
  // Phase 68 — a business's own texts to its guests.
  | 'messaging';

export interface FeatureDefinition {
  /** Dot-notation ID, e.g. `pos.bar_tabs`. Must be unique. */
  id: string;
  module: FeatureModule;
  /**
   * Phase 36.10 — product-suite this feature belongs to. Drives plan-tier
   * entitlement gating in `FeatureFlagService.getFeatureMap()`. `core`
   * features are always available regardless of subscription plan.
   */
  suite: SuiteId;
  /** Human label shown in the flag-matrix dashboard. */
  label: string;
  /** Optional longer description surfaced on hover / settings description slot. */
  description?: string;
  /**
   * Default enabled value when no preset + no per-org override exists. Safe
   * defaults err toward hiding advanced verticals' features from a fresh org.
   */
  default: boolean;
  /** Other feature IDs that must be on for this one to work. */
  dependencies?: string[];
}

const registry = new Map<string, FeatureDefinition>();

/**
 * Register a feature. Called once per feature at module import time. Duplicate
 * IDs throw loudly — silently overriding would hide a conflict between a
 * vertical preset module and a later feature-flag consumer.
 */
export function defineFeature(def: FeatureDefinition): FeatureDefinition {
  if (registry.has(def.id)) {
    throw new Error(`[pos-core] duplicate feature id: ${def.id}`);
  }
  registry.set(def.id, def);
  return def;
}

export function getFeature(id: string): FeatureDefinition | undefined {
  return registry.get(id);
}

export function listFeatures(): FeatureDefinition[] {
  return Array.from(registry.values());
}

export function listFeaturesByModule(module: FeatureModule): FeatureDefinition[] {
  return listFeatures().filter((f) => f.module === module);
}

/** Warn about stored or staged settings without changing the owner's choices. */
export function missingFeatureDependencies(
  feature: Pick<FeatureDefinition, 'id' | 'dependencies'>,
  isEnabled: (id: string) => boolean,
): string[] {
  if (!isEnabled(feature.id)) return [];
  return (feature.dependencies ?? []).filter((id) => !isEnabled(id));
}

/** Intended for tests only — clears the registry. */
export function __resetFeatureRegistry(): void {
  registry.clear();
  registerBuiltInFeatures();
}

// --- Built-in feature declarations --------------------------------------

function registerBuiltInFeatures(): void {
  // POS core surfaces. `pos.checkout` is the only `core` suite member here —
  // it's the universal floor every vertical needs and shouldn't be plan-gated.
  defineFeature({
    id: 'pos.checkout',
    module: 'pos',
    suite: 'core',
    label: 'Checkout',
    description: 'Base cart + payment flow. Every vertical needs this.',
    default: true,
  });
  defineFeature({
    id: 'pos.modifiers',
    module: 'pos',
    suite: 'pos',
    label: 'Modifiers',
    description: 'Per-line modifier picker (default for restaurant / bar).',
    default: true,
  });
  defineFeature({
    id: 'pos.quick_keys',
    module: 'pos',
    suite: 'pos',
    label: 'Quick keys',
    description: 'Device-local pinned-product rail above the grid.',
    default: true,
  });
  defineFeature({
    id: 'pos.open_item',
    module: 'pos',
    suite: 'pos',
    label: 'Open item',
    description: 'Manager-gated ad-hoc line entry.',
    default: true,
  });
  defineFeature({
    // Phase 64 — `table_service` suite (Growth+): the Growth card sells
    // "Bar tabs with Stripe Terminal pre-auth".
    id: 'pos.bar_tabs',
    module: 'pos',
    suite: 'table_service',
    label: 'Bar tabs',
    description: 'Pre-auth-backed bar tabs. Default ON for bar; OFF elsewhere.',
    default: false,
  });
  defineFeature({
    // Phase 64 — `table_service` suite (Growth+): the Growth card sells
    // "Floor plan editor + table management".
    id: 'pos.floor_plan',
    module: 'pos',
    suite: 'table_service',
    label: 'Floor plan',
    description: 'Drag-to-seat table sessions (default for restaurant / bar).',
    default: false,
  });
  defineFeature({
    id: 'pos.gift_cards',
    module: 'pos',
    suite: 'pos',
    label: 'Gift cards',
    description: 'Issue + redeem gift cards (physical + digital).',
    default: true,
  });
  defineFeature({
    id: 'pos.register',
    module: 'pos',
    suite: 'pos',
    label: 'Cash register',
    description: 'Open / close register sessions + cash drops.',
    default: true,
  });
  defineFeature({
    id: 'pos.split_tender',
    module: 'pos',
    suite: 'pos',
    label: 'Split tender',
    description: 'Multi-payment checkout flow.',
    default: true,
  });
  defineFeature({
    // Phase 51.6 — per-course dine-in flow: course picker on cart lines,
    // per-course Fire buttons, KDS course badges. "Held" = not yet
    // fired; the KDS routing dedupe is the state, no hold column.
    id: 'pos.coursing',
    module: 'pos',
    suite: 'pos',
    label: 'Coursing',
    description: 'Course items (apps/entrees/desserts) and fire them to the kitchen per course.',
    default: false,
  });
  defineFeature({
    // Phase 52.2 — item-to-seat: seat picker on the product modal, seat
    // tags on cart lines + KDS tickets. Seats are part of the cart
    // line-collapse identity and feed split-by-seat.
    id: 'pos.seat_ordering',
    module: 'pos',
    suite: 'pos',
    label: 'Seat-level ordering',
    description: 'Tag dine-in items with seat numbers for seat delivery and split-by-seat.',
    default: false,
  });
  defineFeature({
    // Phase 52.3 — persisted split checks: partition an order into
    // independently payable checks (even N-way / by seat), each with its
    // own tenders. Distinct from pos.split_tender (multi-tender against
    // ONE balance).
    id: 'pos.split_checks',
    module: 'pos',
    suite: 'pos',
    label: 'Split checks',
    description: 'Split a table\'s order into separate payable checks (even or by seat).',
    default: false,
  });
  defineFeature({
    // Phase 52.4 — combo/bundle products: a bundle-priced product with
    // component groups ("Pick a side"). Gates the dashboard authoring
    // section; POS surfaces render combo pickers data-driven whenever a
    // combo product exists.
    id: 'pos.combos',
    module: 'pos',
    suite: 'pos',
    label: 'Combos & bundles',
    description: 'Bundle-priced combo products with component choice groups.',
    default: false,
  });
  defineFeature({
    // Phase 52.5 — time-based price rules (happy hour / dayparts).
    // Server pricing applies rules regardless; the flag gates the POS
    // clients' active-rules polling + display mirroring.
    id: 'pos.price_rules',
    module: 'pos',
    suite: 'pos',
    label: 'Price rules',
    description: 'Scheduled happy-hour / daypart pricing on products and categories.',
    default: false,
  });
  defineFeature({
    id: 'pos.barcode_first',
    module: 'pos',
    suite: 'pos',
    label: 'Barcode-first flow',
    description: 'Retail / grocery checkout optimized for scanner-driven entry.',
    default: false,
  });
  defineFeature({
    id: 'pos.weight_pricing',
    module: 'pos',
    suite: 'pos',
    label: 'Weight pricing',
    description: 'Grocery / deli — price-per-unit with scale integration.',
    default: false,
    dependencies: ['pos.barcode_first'],
  });
  defineFeature({
    id: 'pos.plu_at_scale',
    module: 'pos',
    suite: 'pos',
    label: 'PLU at scale',
    description: 'Grocery PLU code lookup — direct numeric entry.',
    default: false,
  });
  defineFeature({
    id: 'pos.age_verification',
    module: 'pos',
    suite: 'pos',
    label: 'Age verification',
    description: 'Prompt for DOB at checkout for age-restricted items.',
    default: false,
  });
  defineFeature({
    // Phase 24.3a — convenience-store quick-tender presets ($5 / $10 /
    // $20 / Exact). Renders a row of preset tiles above the method
    // grid that shortcut straight into cash capture with the preset
    // amount pre-populated. Saves 2-3 seconds per sub-$20 cash sale in
    // a c-store. Off by default; convenience_store preset enables it.
    id: 'pos.quick_tender',
    module: 'pos',
    suite: 'pos',
    label: 'Quick-tender presets',
    description:
      'Cash-denomination preset buttons that shortcut straight to cash capture.',
    default: false,
  });
  defineFeature({
    // Phase 24.3b — "Pay lottery winner" workflow on the register
    // session home. Records a cash drop with type='lottery_payout' +
    // composed winner-info in the reason field; renders a voided
    // receipt for the tape. Off by default; convenience_store preset
    // enables it.
    id: 'pos.lottery_payout',
    module: 'pos',
    suite: 'pos',
    label: 'Lottery payout',
    description:
      'Record manual lottery-winner payouts against the open register session.',
    default: false,
    dependencies: ['pos.register'],
  });
  defineFeature({
    // Phase 44.5 — Tap to Pay on iPhone/Android: the phone IS the card
    // reader (Stripe Terminal local-mobile/tapToPay reader type). Off by
    // default and NOT preset-defaulted — iOS requires the Apple
    // proximity-reader entitlement per deployment (signing fails
    // without it) and Android requires Play-attested builds; see
    // docs/runbooks/tap-to-pay.md before enabling.
    id: 'pos.tap_to_pay',
    module: 'pos',
    suite: 'pos',
    label: 'Tap to Pay on phone',
    description:
      'Accept contactless cards directly on the device — no physical reader.',
    default: false,
  });
  defineFeature({
    // Phase 44.4b — house accounts (charge-to-account AR). POS tender
    // button + the invoicing back-office surface (statements, aging,
    // payment application). Off by default; service_provider preset
    // surfaces it.
    id: 'invoice.house_accounts',
    module: 'invoice',
    suite: 'invoice',
    label: 'House accounts',
    description:
      'Charge-to-account at the register with statements, aging, and payment application.',
    default: false,
  });
  defineFeature({
    // Phase 44.3 — EBT/SNAP tender. Item-driven eligibility enforcement
    // (Phase 24.2c catalog flags) + provider-agnostic processor seam
    // (EBT_PROCESSOR env; simulator for dev/demo, certified vendor
    // adapter when selected). Off by default; grocery + convenience
    // presets surface it. Real acceptance additionally requires the
    // deployment to configure a processor.
    id: 'pos.ebt_snap',
    module: 'pos',
    suite: 'pos',
    label: 'EBT / SNAP tender',
    description:
      'Accept SNAP benefits on eligible items with balance inquiry and split-tender enforcement.',
    default: false,
  });
  defineFeature({
    // Phase 44.2 — dual pricing / cash discounting / credit surcharging.
    // The register applies the org's configured model (cash discount or
    // state-compliant credit surcharge) at tender time, with required
    // disclosures on receipts. Off by default; convenience_store preset
    // (which carries the gas-station capability) enables it. The server
    // resolves per-location compliance — this flag only gates the surface.
    id: 'pos.dual_pricing',
    module: 'pos',
    suite: 'pos',
    label: 'Dual pricing / surcharging',
    description:
      'Cash-discount or credit-surcharge pricing at tender time, with state-rule compliance and receipt disclosures.',
    default: false,
  });
  defineFeature({
    // Phase 24.4 — compact single-pane checkout layout with a bottom-
    // sheet cart overlay. Tablet-portrait optimized for food trucks
    // and other mobile POS environments. Off by default;
    // food_truck preset enables it. Skips the multi-pane layout that
    // the generic + retail checkouts use.
    id: 'pos.compact_checkout',
    module: 'pos',
    suite: 'pos',
    label: 'Compact checkout layout',
    description:
      'Single-pane checkout with a bottom-sheet cart; tablet-portrait layout for mobile POS environments.',
    default: false,
  });
  defineFeature({
    // Phase 24.4 — flip-screen tip flow: the cashier hands the
    // tablet to the customer after payment auth; the tip screen
    // renders full-screen with large touch targets + inverted visual
    // mode so the customer self-selects. Consumer must ALSO have
    // `pos.tips` enabled (resolved at the server — tips feature is
    // not in this registry but comes from the backend flag service).
    // The CheckoutModal short-circuits the flip screen when tips are
    // off, so an org with `pos.customer_facing_tip` on but `pos.tips`
    // off just falls through to a no-tip payment flow.
    id: 'pos.customer_facing_tip',
    module: 'pos',
    suite: 'pos',
    label: 'Customer-facing tip screen',
    description:
      'Hand the tablet to the customer to pick a tip (flip-screen pattern with large targets + inverted mode).',
    default: false,
  });
  defineFeature({
    id: 'pos.pump_integration',
    module: 'pos',
    suite: 'pos',
    label: 'Fuel pump integration',
    description: 'Gas-station forecourt pre-auth + pump release.',
    default: false,
  });
  defineFeature({
    id: 'pos.service_appointment',
    module: 'pos',
    suite: 'pos',
    label: 'Appointment-linked checkout',
    description: 'Service provider — tie order to scheduled appointment.',
    default: false,
  });
  defineFeature({
    // Phase 24.5b — receipt-screen tip-allocation sheet: cashier
    // attributes the captured tip across specific cart lines + the
    // employees who performed them. Total tip is locked at the value
    // captured earlier in checkout; this sheet only rewrites per-line
    // attribution on `pos.tips`.
    //
    // Phase 38 preset realignment (2026-05-24) — moved this from
    // service-provider-only to restaurant + bar default-on. Tips
    // (gratuity money distributed across servers / bartenders on a
    // single check) are a restaurant + bar concept. Service-provider
    // equivalent is commission tracking — different mechanic (% of
    // service revenue paid to the stylist who performed it) — tracked
    // today via custom fields, formal commission feature lands in a
    // future phase. Removed the `dependencies: ['pos.service_appointment']`
    // constraint so any vertical can enable it independently.
    id: 'pos.tip_per_line',
    module: 'pos',
    suite: 'pos',
    label: 'Per-line tip attribution',
    description:
      'After payment, split the captured tip across specific cart lines + the employees who served them. Default-on in restaurant + bar presets.',
    default: false,
  });
  defineFeature({
    // Phase 28.13a — public customer-facing booking page at
    // `/:orgSlug/book` + service-provider appointment-linked checkout.
    // Merchants in verticals that take bookings (restaurant / bar /
    // service_provider) enable this via their preset; retail / grocery /
    // online_store merchants leave it off. Per-location opt-in via the
    // `locations.online_booking_enabled` boolean, so a merchant with
    // this flag on can still stagger the rollout across locations.
    //
    // When the vertical is `service_provider`, the booking page renders
    // a service + stylist variant of the form (service picker first,
    // roster-scoped stylist picker second, duration pre-filled from
    // `products.default_duration_minutes`). When the vertical is
    // restaurant / bar / food_truck, it renders the classic party-size
    // + date + time-slot grid.
    // Phase 64 — `reservations` suite (Scale+), with the staff side below.
    id: 'pos.online_booking',
    module: 'pos',
    suite: 'reservations',
    label: 'Online booking (public page)',
    description:
      'Customer-facing reservation + appointment booking at `/:orgSlug/book` with zero per-cover fees.',
    default: false,
  });
  defineFeature({
    // Phase 64 — the Scale card's "Reservations + Waitlist + Unified Host
    // Stand": the staff-side surfaces (reservation book, waitlist, host
    // stand) on the dashboard and both POS apps. Default ON because those
    // surfaces were ungated on every plan before; the `reservations` plan
    // lock is what turns them off below Scale.
    id: 'pos.reservations',
    module: 'pos',
    suite: 'reservations',
    label: 'Reservations, waitlist + host stand',
    description:
      'Reservation book, waitlist and the unified host stand. Turning it off hides those screens; existing reservations are kept.',
    default: true,
  });
  defineFeature({
    // Phase 64 — the Growth card's "Loyalty program + tiered rewards".
    // Default ON (loyalty was ungated on every plan before); the `loyalty`
    // plan lock turns it off on Starter. When off, the POS makes no
    // loyalty calls and the server accrues no points — checkout itself is
    // never affected.
    id: 'pos.loyalty',
    module: 'pos',
    suite: 'loyalty',
    label: 'Loyalty program',
    description:
      'Points, tiers and rewards: customers earn on every order and redeem at the register.',
    default: true,
  });
  defineFeature({
    // Phase 64 — the Growth card's "Online ordering surface": the public
    // storefront and QR order-and-pay. Default ON (both were ungated on
    // every plan before); the `online_ordering` plan lock turns it off on
    // Starter, where the storefront answers "not available" instead.
    id: 'pos.online_ordering',
    module: 'pos',
    suite: 'online_ordering',
    label: 'Online ordering',
    description:
      'Your online storefront and QR order-and-pay. Each location still opts in from its settings.',
    default: true,
  });
  defineFeature({
    // Phase 65.2 — carrier shipping for online orders, on the merchant's OWN
    // EasyPost account (they pay EasyPost directly). Off by default:
    // shipping takes setup (an account, weights, boxes), and nobody should
    // find it half on.
    id: 'pos.shipping',
    module: 'pos',
    suite: 'online_ordering',
    label: 'Shipping',
    description:
      'Ship online orders with live carrier rates at checkout, through your own EasyPost account.',
    default: false,
  });
  defineFeature({
    // Phase 66.6 — bring-your-own Stripe: a merchant connects the Stripe
    // account they already have, and Stripe bills them directly. On every
    // plan (decision 2). Off while it was piloted one org at a time; on
    // since 66.8 released it (2026-10-05), held equal to
    // `PRODUCT_AVAILABILITY.byoStripe` by featureRegistry.spec.ts.
    id: 'payments.byo_stripe',
    module: 'hardware',
    suite: 'core',
    label: 'Bring your own Stripe',
    description:
      'Take card payments on a Stripe account you already have. Stripe bills that account directly, and Commerce OS names no fee on it.',
    default: true,
  });
  defineFeature({
    // Phase 68.4 — a business registers with the carriers to text its own
    // guests (Settings → Texting). Off for every org until 68.8 releases it
    // (owner, 2026-10-21: a business texts only once its own campaign is
    // approved and it has accepted the texting terms); a pilot org is
    // turned on by hand. Every plan (decision 14), so `core`.
    id: 'messaging.customer_texts',
    module: 'messaging',
    suite: 'core',
    label: 'Texting guests',
    description:
      'Register your business with the carriers, then text guests who agree to it about their order, booking, waitlist spot or visit.',
    default: false,
  });
  defineFeature({
    // Phase 64 — the Scale card's "Custom domain for your storefront".
    id: 'pos.custom_domain',
    module: 'pos',
    suite: 'custom_domains',
    label: 'Custom storefront domain',
    description: 'Serve your storefront from your own domain.',
    default: true,
  });
  defineFeature({
    // Customer records + attach-customer-to-order flow.
    //
    // Phase 38 preset realignment (2026-05-24) — broadened the
    // default-on set from service_provider-only to every loyalty-
    // friendly + repeat-visit vertical: restaurant, bar, retail,
    // grocery, convenience_store, online_store, service_provider.
    // Phase 28 loyalty requires this surface, so the prior "off
    // everywhere except service_provider" default was creating a
    // bad onboarding gap. food_truck deliberately stays off —
    // walk-up velocity at a serving window matters more than per-
    // customer history.
    //
    // Existing orgs that already have customer data keep the records
    // regardless — only the UI surface (dashboard Customers tab + POS
    // attach-customer modal) is gated by this flag. Flipping it on
    // restores the full surface.
    id: 'pos.customers',
    module: 'pos',
    suite: 'pos',
    label: 'Customer records',
    description:
      'Customer CRUD + attach-customer-to-order flow. Customer records are on for most business types, including Quick service and General. Food trucks opt in; their default loyalty flow is online and QR.',
    default: false,
  });
  defineFeature({
    id: 'pos.offline_mode',
    module: 'pos',
    suite: 'core',
    label: 'Offline mode',
    description:
      'SQLite cache + sync queue. Defaults to on — payments remain online-only.',
    default: true,
  });
  defineFeature({
    // Phase 33 Session 33.1 — customer-facing self-service kiosk mode.
    // McDonald's-style ordering kiosk in the merchant's lobby: customer
    // browses + checks out themselves on a stand-mounted tablet. Different
    // from the EMPLOYEE-facing web POS kiosk (Phase 23) — both share the
    // `apps/pos-web` scaffolding but render different shells based on
    // `device.deviceType` (`'kiosk_self_service'` flips the routing
    // dispatcher into the customer-facing path with no PIN auth + idle
    // reset). Off by default; restaurant + bar + food_truck presets enable
    // it because those are the verticals where lobby kiosks pay back the
    // setup cost (8-15% higher avg ticket per McDonald's / Wendy's data).
    // Other merchants opt in via /settings/features.
    id: 'pos.kiosk_self_service',
    module: 'pos',
    suite: 'pos',
    label: 'Self-service kiosk',
    description:
      'Customer-facing tablet kiosk for lobby self-service ordering. Each device flipped to deviceType="kiosk_self_service" boots into the customer-facing shell.',
    default: false,
  });

  defineFeature({
    // Phase 54.3 — second-screen customer-facing display: a paired
    // 'customer_display' device mirrors ONE register's cart over
    // terminal-scoped WS rooms (live order review, running total, and
    // optional tip collection via branding.tips.collectionSurface='cfd').
    id: 'pos.customer_facing_display',
    module: 'pos',
    suite: 'pos',
    label: 'Customer-facing display',
    description:
      'Second-screen guest display paired to a register: live order review, totals, and an optional tip prompt.',
    default: false,
  });

  defineFeature({
    id: 'pos.online_ordering_snooze',
    module: 'pos',
    suite: 'pos',
    label: 'Online ordering snooze',
    description: 'Temporarily pause pickup or delivery ordering by channel.',
    default: false,
  });
  defineFeature({
    id: 'pos.shift_logbook',
    module: 'pos',
    suite: 'pos',
    label: 'Shift logbook',
    description: 'Manager handoff notes timeline for shift operations.',
    default: false,
  });
  defineFeature({
    id: 'pos.broadcast_messages',
    module: 'pos',
    suite: 'pos',
    label: 'POS broadcast messages',
    description: 'Broadcast manager messages to all connected POS terminals.',
    default: false,
  });
  defineFeature({
    // Phase 33.3 — deliberately default-off with NO preset enablement:
    // taking live calls requires a provisioned inbound number + a
    // telephony provider adapter, so a merchant opts in explicitly from
    // /settings/features once their number is mapped.
    id: 'pos.voice_ordering',
    module: 'pos',
    suite: 'pos',
    label: 'AI voice ordering',
    description:
      'Phone-based AI ordering agent: callers order in natural language (English/Spanish), the agent confirms and submits straight to the KDS as a pickup order.',
    default: false,
  });
  defineFeature({
    // Phase 37 Session 37.1 — driver-recommendation menu upsell rules.
    // Merchants author per-org rules linking a driver (specific product OR
    // an entire category) to a recommendation product, scoped to one or
    // more channels via `surfaces text[]` ('pos' | 'kiosk' | 'storefront').
    // The pos-core resolver `resolveUpsellSuggestions` runs entirely
    // client-side on the cart at render time — no per-cart server roundtrip.
    // Off by default; restaurant + bar + food_truck presets enable it because
    // those verticals see the largest avg-ticket lift from upsell rails
    // (Toast reports 8-12% — food trucks especially because the limited
    // menu makes "add fries / a drink" a very natural ask at the window).
    // Retail / grocery / convenience / service-provider merchants opt in
    // via /settings/features. The 33.1c kiosk `branding.kiosk.upsellProductIds`
    // flat-list rail migrates onto this engine in 37.5 (the closing 37.x
    // sub-session). Phase 38 preset realignment (2026-05-24) added
    // food_truck as a default-on preset.
    id: 'pos.menu_upsells',
    module: 'pos',
    suite: 'pos',
    label: 'Menu upsells',
    description:
      'Driver→recommendation upsell rules surfaced on POS / kiosk / storefront cart. Authored from /settings/upsells. Default-on for restaurant, bar, and food_truck presets.',
    default: false,
  });
  defineFeature({
    // Phase 33 Session 33.4 — handheld / tableside ordering for servers.
    // Lightweight phone/small-tablet surface that shortcuts the
    // server's "walk to terminal → ring → walk back" loop into
    // "tap table → ring at the table → send to kitchen". The order
    // fires through the existing checkout pipeline (createOrder +
    // addOrderItem + checkoutOrder) so KDS routing + reporting +
    // table-session linkage all flow through unchanged. Payment
    // happens later from the register through the floor-plan session.
    // Restaurant and bar enable this by default; other businesses opt in.
    // Phase 67 removes the chair/field defaults because this surface needs
    // a table session. Migration0236 preserves existing choices.
    // Phase 64 — `table_service` suite (Growth+): the handheld table
    // picker reads the floor plan and orders on a table session, so it
    // cannot work where the floor plan is locked.
    id: 'pos.handheld_mode',
    module: 'pos',
    suite: 'table_service',
    label: 'Handheld / tableside ordering',
    description:
      'Server-facing tablet/phone surface for taking orders at the table + sending straight to kitchen. Payment happens later from the register. Default-on for restaurant and bar presets.',
    default: false,
  });

  // Kitchen / KDS
  defineFeature({
    id: 'kds.stations',
    module: 'kds',
    suite: 'kds',
    label: 'KDS stations',
    description: 'Kitchen display rooms + real-time bump/recall.',
    default: false,
  });

  defineFeature({
    // Phase 54.4 — kitchen printer tickets beside the KDS screens: the
    // web-KDS device pairs a WebUSB ESC/POS printer and auto-prints
    // tickets for its configured stations from the same order:new room
    // events it renders. Station→printer mapping is per-DEVICE local
    // config (the printer is browser-attached hardware the server can't
    // reach); this flag gates the print settings + reprint surfaces.
    id: 'kds.printer_tickets',
    module: 'kds',
    suite: 'kds',
    label: 'Kitchen printer tickets',
    description:
      'Auto-print kitchen tickets from the web KDS on a USB ESC/POS printer, per station.',
    default: false,
  });

  // Tax — Phase 64. The multi-jurisdiction layer the Growth card sells as
  // "Tax engine (multi-jurisdiction)". Location rates are NOT part of it:
  // every plan sets them, and checkout calculation is never gated.
  defineFeature({
    id: 'tax.multi_jurisdiction',
    module: 'tax',
    suite: 'tax_engine',
    label: 'Tax engine (multi-jurisdiction)',
    description:
      'Tax codes by category, exemption certificates and the by-jurisdiction tax report. Turning it off hides those screens; checkout keeps charging the rates already configured.',
    default: true,
  });

  // Invoicing
  defineFeature({
    id: 'invoice.quotes',
    module: 'invoice',
    suite: 'invoice',
    label: 'Quotes',
    description: 'Quote → invoice → payment portal.',
    default: true,
  });

  // Payroll
  // Basic time clock (clock in/out + breaks) is `core` so it's available on
  // every plan — Starter + Growth merchants with employees need it. Advanced
  // payroll (scheduling + payroll runs) is gated server-side by the
  // `payroll` suite via @RequiresSuite on PayrollController, NOT this flag.
  defineFeature({
    id: 'payroll.time_clock',
    module: 'payroll',
    suite: 'core',
    label: 'Time clock',
    description: 'Clock in/out + breaks on the POS terminal.',
    default: true,
  });

  // Phase 47.0 — Employee Self-Service inside the POS apps. `core` suite:
  // schedule/hours/requests ride the same entitlement as the time clock
  // (the schedule READ endpoint still carries @RequiresSuite('payroll')
  // server-side, so My Schedule empty-states cleanly on plans without the
  // payroll suite). ESS is password-session-only — PIN sessions never see
  // the surface regardless of this flag.
  defineFeature({
    id: 'payroll.ess',
    module: 'payroll',
    suite: 'core',
    label: 'Employee self-service',
    description:
      'Employee Self-Service surface in the POS apps: my schedule, hours, requests, tips, profile.',
    default: true,
  });

  // Pay-data exposure is merchant opt-in — paychecks reach employee
  // devices only after the org flips this on (default off; no preset
  // enables it).
  defineFeature({
    id: 'payroll.ess_pay',
    module: 'payroll',
    suite: 'core',
    label: 'Employee pay visibility',
    description:
      'Show approved paychecks (internal payroll runs + accounting-integration pay data) in Employee Self-Service.',
    default: false,
    dependencies: ['payroll.ess'],
  });

  // Phase 48.2 — published open (unassigned) shifts claimable from ESS.
  // Rides the payroll suite (the claim endpoints carry
  // @RequiresSuite('payroll')); claim approval defaults to a manager
  // queue (settings.scheduling.auto_approve_claims opts into instant
  // assignment).
  defineFeature({
    id: 'payroll.open_shifts',
    module: 'payroll',
    suite: 'payroll',
    label: 'Open shift claims',
    description:
      'Let employees browse and claim unassigned published shifts from Employee Self-Service.',
    default: true,
    dependencies: ['payroll.ess'],
  });

  // Inventory
  defineFeature({
    id: 'inventory.tracking',
    module: 'inventory',
    suite: 'inventory',
    label: 'Inventory tracking',
    description: 'Stock items, vendors, purchase orders, receiving + waste.',
    default: true,
  });

  defineFeature({
    id: 'inventory.cycle_counts',
    module: 'inventory',
    suite: 'inventory',
    label: 'Cycle counts',
    description:
      'Scoped physical-count sessions with frozen expected quantities, blind counting, and variance approval.',
    default: true,
    dependencies: ['inventory.tracking'],
  });

  defineFeature({
    // Phase 64 — the Scale card's "Recipe costing + menu engineering".
    // Turning it off hides the recipe, menu-engineering and food-cost
    // screens; recipe-driven stock depletion keeps running.
    id: 'inventory.recipe_costing',
    module: 'inventory',
    suite: 'recipe_costing',
    label: 'Recipe costing + menu engineering',
    description:
      'Recipes and sub-recipes with live ingredient costs, menu-engineering quadrants and food-cost reports.',
    default: true,
  });

  defineFeature({
    id: 'accounting.general_ledger',
    module: 'accounting',
    suite: 'accounting',
    label: 'General ledger',
    description:
      'Double-entry books: chart of accounts, automatic journals from sales, inventory, and labor, period close, and financial statements.',
    // OFF by default — enabling it starts a set of books the operator
    // becomes responsible for. That is a deliberate act, not a default.
    default: false,
  });

  defineFeature({
    id: 'inventory.invoice_scanning',
    module: 'inventory',
    suite: 'inventory',
    label: 'Invoice scanning',
    description:
      'Photograph supplier invoices to receive stock and draft purchase orders.',
    default: true,
    dependencies: ['inventory.tracking'],
  });

  // Marketing
  defineFeature({
    id: 'marketing.campaigns',
    module: 'marketing',
    suite: 'marketing',
    label: 'Marketing campaigns',
    description: 'Email campaigns + customer segmentation.',
    default: true,
  });

  // Reporting / AI. The analytics assistant's read tools ship on by default
  // (gated by the `reporting` suite); AI *write* actions are off by default
  // and must be opted into per-org — they let the agent change live data
  // (86 an item, snooze online ordering) behind human-in-the-loop
  // confirmation + RBAC. Conservative default per the events/AI safety bar.
  defineFeature({
    id: 'reporting.ai_write_actions',
    module: 'reporting',
    suite: 'reporting',
    label: 'AI write actions',
    description:
      'Let the analytics assistant make confirmed, reversible changes ' +
      '(86 items, snooze ordering) — each requires explicit user approval.',
    default: false,
  });

  // Phase 46.2 — per-org kill-switch for the conversational AI assistant
  // (chat + setup concierge + TopBar launcher). Default ON: the assistant is
  // already live for every Growth+ org via the `reporting` suite — migration
  // 0096 deliberately made AI a Growth+ capability, so do NOT re-gate it to
  // Scale. This flag only lets an org turn the assistant OFF. Disabling stops
  // new chat turns (server 403s); saved chat history stays readable.
  defineFeature({
    id: 'analytics.assistant',
    module: 'reporting',
    suite: 'reporting',
    label: 'AI assistant',
    description:
      'The conversational AI assistant (analytics chat + setup concierge). ' +
      'On by default for Growth+ orgs — turn off to disable new chats; ' +
      'past conversations stay readable.',
    default: true,
  });

  // Phase 64 — the Scale card's "AI benchmarking". The rest of the
  // analytics suite (assistant, forecasts) stays Growth.
  defineFeature({
    id: 'analytics.benchmarking',
    module: 'reporting',
    suite: 'benchmarking',
    label: 'AI benchmarking',
    description: 'Rank your numbers against anonymized peers in your vertical.',
    default: true,
  });

  // Phase 64 — the Scale card's "Audit log + GDPR-compliant data export".
  // The log is WRITTEN on every plan (it is a security record); this gates
  // reading and exporting it. A person's own data export and erasure are
  // never gated.
  defineFeature({
    id: 'audit.log',
    module: 'reporting',
    suite: 'audit_log',
    label: 'Audit log',
    description:
      'Browse and export who changed what, per-record change history, and access reviews.',
    default: true,
  });

  // Phase 64 — the Enterprise card's "SSO / SAML". Gates configuring SSO
  // and SCIM; sign-in through an existing connection keeps working.
  defineFeature({
    id: 'auth.sso',
    module: 'auth',
    suite: 'sso',
    label: 'SSO / SAML',
    description: 'Sign in through your identity provider, with SCIM user provisioning.',
    default: true,
  });

  // Field service (Phase 50) — the `field_services` vertical's suite: crews
  // dispatched to the customer's property. Every flag defaults OFF; the
  // vertical preset turns the first nine on for a fresh field-service org,
  // while `crm` + `alerts` stay opt-in (a two-person crew doesn't want a
  // sales pipeline or an escalation queue on day one). All flags ride the
  // `field_service` billing suite (Scale + Enterprise).
  defineFeature({
    id: 'field_service.core',
    module: 'field_service',
    suite: 'field_service',
    label: 'Field service core',
    description:
      'Client service addresses, service zones, bookable-service settings, and the jobs list. The floor every other field-service flag stands on.',
    default: false,
  });
  defineFeature({
    id: 'field_service.quoting',
    module: 'field_service',
    suite: 'field_service',
    label: 'Field quoting',
    description:
      'Acreage / crew-day / flat quote lines, mobilization fees, deposit percentages, and client approval links with e-signature.',
    default: false,
    dependencies: ['field_service.core'],
  });
  defineFeature({
    id: 'field_service.visits',
    module: 'field_service',
    suite: 'field_service',
    label: 'Visits & booking',
    description:
      'Scheduled at-address visits (estimate + job kinds), the visits calendar, and the public self-booking page with live slot availability.',
    default: false,
    dependencies: ['field_service.core'],
  });
  defineFeature({
    id: 'field_service.dispatch',
    module: 'field_service',
    suite: 'field_service',
    label: 'Dispatch board',
    description:
      'Technician-lane day board with drag-to-reassign and bulk weather-day rescheduling.',
    default: false,
    dependencies: ['field_service.visits'],
  });
  defineFeature({
    id: 'field_service.work_orders',
    module: 'field_service',
    suite: 'field_service',
    label: 'Work orders & checklists',
    description:
      'Versioned work orders per job with scope, access notes, hazards, and snapshot checklists (photo-gated items supported).',
    default: false,
    dependencies: ['field_service.core'],
  });
  defineFeature({
    id: 'field_service.change_orders',
    module: 'field_service',
    suite: 'field_service',
    label: 'Change orders',
    description:
      'Office- and field-raised change orders: unpriced field drafts, office pricing, client approval, and automatic invoicing.',
    default: false,
    dependencies: ['field_service.quoting'],
  });
  defineFeature({
    id: 'field_service.agreements',
    module: 'field_service',
    suite: 'field_service',
    label: 'Service agreements',
    description:
      'Recurring service agreements with weekly/monthly cadence, seasonal months, rolling visit generation, and per-visit or monthly billing.',
    default: false,
    dependencies: ['field_service.visits'],
  });
  defineFeature({
    id: 'field_service.media',
    module: 'field_service',
    suite: 'field_service',
    label: 'Job media library',
    description:
      'Before/progress/after photo library with immutable originals, annotation versions, checksums, and marketing-consent tracking.',
    default: false,
    dependencies: ['field_service.core'],
  });
  defineFeature({
    id: 'field_service.costing',
    module: 'field_service',
    suite: 'field_service',
    label: 'Job costing & P&L',
    description:
      'Labor attribution, burdened cost rates, expenses, per-job P&L, employee performance, and monthly close snapshots.',
    default: false,
    dependencies: ['field_service.core'],
  });
  defineFeature({
    id: 'field_service.crm',
    module: 'field_service',
    suite: 'field_service',
    label: 'Sales pipeline',
    description:
      'Deal pipeline with stages, SLAs, stale-lead detection, and lost-reason tracking. Opt-in — not part of the vertical preset.',
    default: false,
    dependencies: ['field_service.core'],
  });
  defineFeature({
    id: 'field_service.alerts',
    module: 'field_service',
    suite: 'field_service',
    label: 'Alerts & escalation',
    description:
      'Claimable internal alerts (new leads, failed payments, pending change orders) with re-ping escalation and a morning digest. Opt-in.',
    default: false,
    dependencies: ['field_service.core'],
  });
}

registerBuiltInFeatures();
